import React, { useEffect, useState } from "react";
import axios from "axios";
import { baseApiURL } from "../../baseUrl";
import { sortEnrollmentNo } from "../../utils/enrollmentSorter";
import toast from "react-hot-toast";
import { FiAlertCircle } from "react-icons/fi";

const AddAttendance = ({ branch: lockedBranch }) => {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [markedAttendance, setMarkedAttendance] = useState({});
  const [noStudentsMessage, setNoStudentsMessage] = useState("");
  const [semester, setSemester] = useState("-- Select --");
  const [branch, setBranch] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [filteredSubjects, setFilteredSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("-- Select --");
  const [selectedBranch, setSelectedBranch] = useState(lockedBranch || "-- Select --");
  const [selectedSection, setSelectedSection] = useState("-- Select --");
  const [selectedPeriod, setSelectedPeriod] = useState("-- Select --");
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [range, setRange] = useState({ start: "", end: "" });
  const [totalClasses, setTotalClasses] = useState("");
  const [selectedSubjectId, setSelectedSubjectId] = useState(null);
  const [selectedRegulation, setSelectedRegulation] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [canAddAttendance, setCanAddAttendance] = useState(false);
  const [absenteesInput, setAbsenteesInput] = useState("");
  const [attendanceExists, setAttendanceExists] = useState(false);
  const [existingAttendanceRecords, setExistingAttendanceRecords] = useState([]);
  const [isCheckingAttendance, setIsCheckingAttendance] = useState(false);

  // Dynamic Sections available for filtering
  const [sections, setSections] = useState(['A', 'B', 'C', 'D']);

  useEffect(() => {
    const fetchSections = async () => {
      try {
        let params = {};
        if (selectedBranch && selectedBranch !== "-- Select --") params.branch = selectedBranch;
        if (semester && semester !== "-- Select --") params.semester = semester;
        const res = await axios.get(`${baseApiURL()}/section/getSectionsByBranchAndSemester`, { params });
        if (res.data.success && res.data.sections?.length > 0) {
          setSections(res.data.sections);
        }
      } catch (err) {
        console.error("Error fetching dynamic sections:", err);
      }
    };
    fetchSections();
  }, [selectedBranch, semester]);

  
  // Fetch branch data
  const getBranchData = () => {
    axios
      .get(`${baseApiURL()}/branch/getBranch`)
      .then((response) => {
        if (response.data.success) {
          setBranch(response.data.branches);
        } else {
          toast.error(response.data.message);
        }
      })
      .catch((error) => {
        console.error(error);
        toast.error(error.message);
      });
  };

  
  // Fetch subject data
  const getSubjectData = () => {
    setLoading(true);
    toast.loading("Loading Subjects");
    axios
      .get(`${baseApiURL()}/subject/getSubject`)
      .then((response) => {
        toast.dismiss();
        setLoading(false);
        if (response.data.success) {
          setSubjects(response.data.subject);
          filterSubjectsBySemester(semester, response.data.subject);
        } else {
          toast.error(response.data.message);
        }
      })
      .catch((error) => {
        toast.dismiss();
        setLoading(false);
        toast.error(error.message);
      });
  };

  // Function to filter subjects based on semester and branch
  const filterSubjectsBySemester = (selectedSemester, subjectsList = subjects, reg = selectedRegulation) => {
    if (selectedSemester === "-- Select --" || selectedBranch === "-- Select --" || noStudentsMessage) {
      setFilteredSubjects([]);
      setSelectedSubject("-- Select --");
      setSelectedSubjectId(null);
      setTotalClasses("");
      setCanAddAttendance(false);
      return;
    }

    const semesterSubjects = subjectsList.filter(
      (subject) => 
        String(subject.semester) === String(selectedSemester) &&
        (subject.branch?.name === selectedBranch || subject.branch === selectedBranch) &&
        (!reg || subject.regulation?.toUpperCase() === reg.toUpperCase())
    );
    setFilteredSubjects(semesterSubjects);
    
    // If we had a subject selected before, try to preserve it
    if (selectedSubject !== "-- Select --") {
      const preservedSubject = semesterSubjects.find(sub => sub.name === selectedSubject);
      if (preservedSubject) {
        setSelectedSubjectId(preservedSubject._id);
        // Get the section-specific total
        getSectionTotal(preservedSubject._id, selectedSection);
        setCanAddAttendance(false);
      } else {
        setSelectedSubject("-- Select --");
        setSelectedSubjectId(null);
        setTotalClasses("");
        setCanAddAttendance(false);
      }
    } else {
      setSelectedSubject("-- Select --");
      setSelectedSubjectId(null);
      setTotalClasses("");
      setCanAddAttendance(false);
    }
  };

  // Get section total from backend
  const getSectionTotal = async (subjectId, section) => {
    if (subjectId && section !== "-- Select --") {
      try {
        const response = await axios.get(`${baseApiURL()}/subject/getSectionTotal/${subjectId}/${section}`);
        if (response.data.success) {
          setTotalClasses(response.data.sectionTotal.toString());
        }
      } catch (error) {
        console.error("Error fetching section total:", error);
        setTotalClasses("0");
      }
    } else {
      setTotalClasses("");
    }
  };

  // Update section total in backend
  const updateSectionTotal = async (newTotal) => {
    if (!selectedSubjectId || selectedSection === "-- Select --" || newTotal === undefined) {
      toast.error("Please select a subject, section and enter total classes");
      return;
    }
    
    setLoading(true);
    toast.loading("Updating total classes...");
    try {
      const response = await axios.put(`${baseApiURL()}/subject/updateSectionTotal/${selectedSubjectId}`, {
        section: selectedSection,
        total: Number(newTotal),
        isIncrement: false, // Set to false for absolute value updates
        incrementType: 'BY_VALUE'
      });
      
      toast.dismiss();
      setLoading(false);
      if (response.data.success) {
        toast.success(`Section ${selectedSection} total classes updated successfully!`);
        setCanAddAttendance(true);
        
        // Update local state
        const updatedSubjects = subjects.map(sub => {
          if (sub._id === selectedSubjectId) {
            const updatedSectionTotals = [...sub.sectionTotals];
            const sectionIndex = updatedSectionTotals.findIndex(s => s.section === selectedSection);
            if (sectionIndex >= 0) {
              updatedSectionTotals[sectionIndex].total = Number(newTotal);
            } else {
              updatedSectionTotals.push({ section: selectedSection, total: Number(newTotal) });
            }
            return { ...sub, sectionTotals: updatedSectionTotals };
          }
          return sub;
        });
        setSubjects(updatedSubjects);
        
        const updatedFilteredSubjects = filteredSubjects.map(sub => {
          if (sub._id === selectedSubjectId) {
            const updatedSectionTotals = [...sub.sectionTotals];
            const sectionIndex = updatedSectionTotals.findIndex(s => s.section === selectedSection);
            if (sectionIndex >= 0) {
              updatedSectionTotals[sectionIndex].total = Number(newTotal);
            } else {
              updatedSectionTotals.push({ section: selectedSection, total: Number(newTotal) });
            }
            return { ...sub, sectionTotals: updatedSectionTotals };
          }
          return sub;
        });
        setFilteredSubjects(updatedFilteredSubjects);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.dismiss();
      setLoading(false);
      toast.error("Failed to update total classes");
      console.error("Update error:", error.response?.data || error.message);
    }
  };

  // Handle semester change
  const handleSemesterChange = (e) => {
    const newSemester = e.target.value;
    setSemester(newSemester);
    // Reset regulation when semester changes to allow for fresh detection
    setSelectedRegulation("");
    filterSubjectsBySemester(newSemester);
  };

  // Handle branch change
  const handleBranchChange = (e) => {
    const newBranch = e.target.value;
    setSelectedBranch(newBranch);
    filterSubjectsBySemester(semester);
  };

  // Handle regulation change (internal use)
  const handleRegulationChange = (e) => {
    const newReg = e.target.value.toUpperCase();
    setSelectedRegulation(newReg);
    filterSubjectsBySemester(semester, subjects);
  };

  // Handle section change
  const handleSectionChange = (e) => {
    const newSection = e.target.value;
    setSelectedSection(newSection);
    
    // When section changes, update the total classes for the selected subject and section
    if (selectedSubjectId && newSection !== "-- Select --") {
      getSectionTotal(selectedSubjectId, newSection);
    } else {
      setTotalClasses("");
    }
    setCanAddAttendance(false);
  };

  // Handle subject change
  const handleSubjectChange = (e) => {
    const selectedValue = e.target.value;
    setSelectedSubject(selectedValue);
    
    if (selectedValue === "-- Select --") {
      setSelectedSubjectId(null);
      setTotalClasses("");
      setCanAddAttendance(false);
      return;
    }

    const subject = filteredSubjects.find(sub => sub.name === selectedValue);
    if (subject) {
      setSelectedSubjectId(subject._id);
      // Get the section-specific total
      getSectionTotal(subject._id, selectedSection);
      setCanAddAttendance(false);
    } else {
      setSelectedSubjectId(null);
      setTotalClasses("");
      setCanAddAttendance(false);
    }
  };

  // Increment total classes by 1 only - FIXED
  const incrementTotalClasses = async () => {
    if (!selectedSubjectId || selectedSection === "-- Select --") {
      toast.error("Please select a subject and section first");
      return;
    }
    
    // Check if attendance already exists for this date/branch/semester/period/section
    if (attendanceExists) {
      toast.error("Attendance already exists for this date/subject/period/section. No need to increment total classes.");
      return;
    }
    
    setLoading(true);
    toast.loading("Incrementing total classes by 1...");
    
    try {
      // Use the new endpoint that increments by 1 only
      const response = await axios.put(
        `${baseApiURL()}/subject/incrementSectionTotalByOne/${selectedSubjectId}`,
        { section: selectedSection }
      );
      
      toast.dismiss();
      setLoading(false);
      
      if (response.data.success) {
        // Update local state with the new total
        const newTotal = response.data.sectionTotal;
        setTotalClasses(newTotal.toString());
        toast.success("Total classes incremented by 1");
        setCanAddAttendance(true);
        
        // Update local subjects state
        const updatedSubjects = subjects.map(sub => {
          if (sub._id === selectedSubjectId) {
            const updatedSectionTotals = [...sub.sectionTotals];
            const sectionIndex = updatedSectionTotals.findIndex(s => s.section === selectedSection);
            if (sectionIndex >= 0) {
              updatedSectionTotals[sectionIndex].total = newTotal;
            } else {
              updatedSectionTotals.push({ section: selectedSection, total: newTotal });
            }
            return { ...sub, sectionTotals: updatedSectionTotals };
          }
          return sub;
        });
        setSubjects(updatedSubjects);
        
        const updatedFilteredSubjects = filteredSubjects.map(sub => {
          if (sub._id === selectedSubjectId) {
            const updatedSectionTotals = [...sub.sectionTotals];
            const sectionIndex = updatedSectionTotals.findIndex(s => s.section === selectedSection);
            if (sectionIndex >= 0) {
              updatedSectionTotals[sectionIndex].total = newTotal;
            } else {
              updatedSectionTotals.push({ section: selectedSection, total: newTotal });
            }
            return { ...sub, sectionTotals: updatedSectionTotals };
          }
          return sub;
        });
        setFilteredSubjects(updatedFilteredSubjects);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.dismiss();
      setLoading(false);
      toast.error("Failed to increment total classes");
      console.error("Increment error:", error.response?.data || error.message);
    }
  };

  // Decrement total classes and disable attendance if needed
  const decrementTotalClasses = () => {
    if (!selectedSubjectId || selectedSection === "-- Select --" || Number(totalClasses) <= 0) {
      return;
    }
    
    const newTotal = Number(totalClasses) - 1;
    setTotalClasses(newTotal.toString());
    updateSectionTotal(newTotal);
    
    // If we're decrementing to 0, disable attendance
    if (newTotal === 0) {
      setCanAddAttendance(false);
    }
  };

  // Manual total classes update
  const handleTotalClassesChange = (e) => {
    const value = e.target.value;
    setTotalClasses(value);
    // Don't auto-update here, let user use +/- buttons or manually submit
  };

  // Manual update of total classes
  const updateTotalClassesManually = () => {
    if (!selectedSubjectId || selectedSection === "-- Select --") {
      toast.error("Please select a subject and section first");
      return;
    }
    
    updateSectionTotal(Number(totalClasses));
  };

  // Check if attendance already exists for the selected filters
  const checkAttendanceExists = async () => {
    if (!selectedBranch || selectedBranch === "-- Select --" || 
        !semester || semester === "-- Select --" || 
        !selectedSection || selectedSection === "-- Select --" || 
        !selectedSubject || selectedSubject === "-- Select --" || 
        !selectedPeriod || selectedPeriod === "-- Select --" || 
        !selectedDate) {
      return;
    }

    setIsCheckingAttendance(true);
    try {
      const response = await axios.get(`${baseApiURL()}/attendence/checkExists`, {
        params: {
          branch: selectedBranch,
          semester: semester,
          period: selectedPeriod,
          section: selectedSection,
          subject: selectedSubject,
          date: selectedDate
        }
      });

      if (response.data.success) {
        setAttendanceExists(response.data.exists);
        if (response.data.exists) {
          // Fetch existing attendance records
          const existingResponse = await axios.get(`${baseApiURL()}/attendence/getExisting`, {
            params: {
              branch: selectedBranch,
              semester: semester,
              period: selectedPeriod,
              section: selectedSection,
              subject: selectedSubject,
              date: selectedDate
            }
          });

          if (existingResponse.data.success) {
            setExistingAttendanceRecords(existingResponse.data.attendanceRecords);
            // Pre-select students who already have attendance
            const preSelectedAttendance = {};
            existingResponse.data.attendanceRecords.forEach(record => {
              preSelectedAttendance[record.enrollmentNo] = {
                enrollmentNo: record.enrollmentNo,
                name: record.name,
                branch: record.branch,
                section: record.section,
                subject: record.subject,
                period: record.period,
                semester: record.semester,
                date: record.date,
              };
            });
            setMarkedAttendance(preSelectedAttendance);
            
            // Update select all checkbox state
            const allStudentsSelected = filteredStudents.length > 0 && 
              filteredStudents.every(student => preSelectedAttendance[student.enrollmentNo]);
            setSelectAllChecked(allStudentsSelected);
          }
        } else {
          setExistingAttendanceRecords([]);
          setMarkedAttendance({});
          setSelectAllChecked(false);
        }
      }
    } catch (error) {
      console.error("Error checking attendance existence:", error);
      toast.error("Failed to check existing attendance");
    } finally {
      setIsCheckingAttendance(false);
    }
  };

  // Fetch student data
  useEffect(() => {
    const fetchStudents = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${baseApiURL()}/student/details/getDetails2`);
        setLoading(false);
        if (response.data.success) {
          setStudents(response.data.students);
        } else {
          toast.error("Failed to load students");
        }
      } catch (error) {
        setLoading(false);
        toast.error("Error fetching students");
        console.error(error);
      }
    };

    fetchStudents();
  }, []);

  // Initial data fetch
  useEffect(() => {
    getBranchData();
    getSubjectData();
  }, []);

  // Filter students based on filters
  useEffect(() => {
    filterStudents();
  }, [students, selectedBranch, semester, selectedSection, range]);

  // Check attendance existence when filters change
  useEffect(() => {
    checkAttendanceExists();
  }, [selectedBranch, semester, selectedSection, selectedSubject, selectedPeriod, selectedDate]);

  // Auto-detect regulation and check student presence for selected semester & branch
  useEffect(() => {
    if (semester !== "-- Select --" && selectedBranch !== "-- Select --") {
      const semesterStudents = students.filter(
        (s) => String(s.semester) === String(semester) &&
               (s.branch.toLowerCase() === selectedBranch.toLowerCase() || s.branch === selectedBranch)
      );

      if (semesterStudents.length > 0) {
        const detectedReg = semesterStudents[0].regulation || "";
        setSelectedRegulation(detectedReg.toUpperCase());
        setNoStudentsMessage("");
      } else {
        setSelectedRegulation("");
        setNoStudentsMessage("no students are there for that semester");
      }
    } else {
      setSelectedRegulation("");
      setNoStudentsMessage("");
    }
  }, [semester, selectedBranch, students]);

  // Re-filter subjects whenever semester, selectedBranch, selectedRegulation, or subjects update
  useEffect(() => {
    filterSubjectsBySemester(semester, subjects, selectedRegulation);
  }, [semester, selectedBranch, selectedRegulation, subjects, noStudentsMessage]);

  const filterStudents = () => {
    let filtered = students;

    if (selectedBranch && selectedBranch !== "-- Select --") {
      filtered = filtered.filter(
        (student) => student.branch.toLowerCase() === selectedBranch.toLowerCase()
      );
    }

    if (semester && semester !== "-- Select --") {
      filtered = filtered.filter((student) => String(student.semester) === semester);
    }

    if (selectedSection && selectedSection !== "-- Select --") {
      filtered = filtered.filter((student) => student.section === selectedSection);
    }

    if (range.start && range.end) {
      filtered = filtered.filter(
        (student) =>
          student.enrollmentNo >= Number(range.start) &&
          student.enrollmentNo <= Number(range.end)
      );
    }

    filtered.sort(sortEnrollmentNo);

    setFilteredStudents(filtered);
  };

  // Toggle individual attendance (local state only)
  const toggleAttendance = (student) => {
    // Allow editing if attendance already exists, otherwise require total class increment
    if (!attendanceExists && !canAddAttendance) {
      toast.error("Please increment total classes first to enable attendance marking");
      return;
    }
    if (selectedSubject === "-- Select --" || selectedPeriod === "-- Select --") {
      toast.error("Please select both a subject and period.");
      return;
    }
    
    // Check if student is marked as absent
    const absentees = absenteesInput.split(',').map(enrollment => enrollment.trim()).filter(enrollment => enrollment);
    if (absentees.includes(student.enrollmentNo)) {
      toast.error("Cannot mark attendance for absent student. Remove from absentees list first.");
      return;
    }
    
    setMarkedAttendance((prev) => {
      const newState = { ...prev };
      if (newState[student.enrollmentNo]) {
        delete newState[student.enrollmentNo];
      } else {
        newState[student.enrollmentNo] = {
          enrollmentNo: student.enrollmentNo,
          name: `${student.firstName} ${student.middleName ? student.middleName + ' ' : ''}${student.lastName}`.trim(),
          branch: student.branch,
          section: student.section,
          subject: selectedSubject,
          period: selectedPeriod,
          semester: semester,
          date: selectedDate,
        };
      }
      return newState;
    });
  };

  // Mark/unmark all locally
  const toggleSelectAll = () => {
    // Allow editing if attendance already exists, otherwise require total class increment
    if (!attendanceExists && !canAddAttendance) {
      toast.error("Please increment total classes first to enable attendance marking");
      return;
    }
    if (selectedSubject === "-- Select --" || selectedPeriod === "-- Select --") {
      toast.error("Please select both a subject and period.");
      return;
    }
    const newSelectAllChecked = !selectAllChecked;
    setSelectAllChecked(newSelectAllChecked);
    if (newSelectAllChecked) {
      const attendanceDataForBulk = {};
      // Get absentees from input
      const absentees = absenteesInput.split(',').map(enrollment => enrollment.trim()).filter(enrollment => enrollment);
      
      filteredStudents.forEach((student) => {
        // Skip absentees when marking all
        if (!absentees.includes(student.enrollmentNo)) {
          attendanceDataForBulk[student.enrollmentNo] = {
            enrollmentNo: student.enrollmentNo,
            name: `${student.firstName} ${student.middleName ? student.middleName + ' ' : ''}${student.lastName}`.trim(),
            branch: student.branch,
            section: student.section,
            subject: selectedSubject,
            period: selectedPeriod,
            semester: semester,
            date: selectedDate,
          };
        }
      });
      setMarkedAttendance(attendanceDataForBulk);
    } else {
      setMarkedAttendance({});
    }
  };

  // Handle absentees input
  const handleAbsenteesInput = (input) => {
    setAbsenteesInput(input);
    
    if (!input.trim()) {
      return;
    }
    
    const absentees = input.split(',').map(enrollment => enrollment.trim()).filter(enrollment => enrollment);
    
    const newAttendance = {};
    Object.keys(markedAttendance).forEach(enrollmentNo => {
      if (!absentees.includes(enrollmentNo)) {
        newAttendance[enrollmentNo] = markedAttendance[enrollmentNo];
      }
    });
    
    setMarkedAttendance(newAttendance);
    
    const remainingStudents = filteredStudents.filter(student => !absentees.includes(student.enrollmentNo));
    const allRemainingSelected = remainingStudents.length > 0 && 
      remainingStudents.every(student => newAttendance[student.enrollmentNo]);
    setSelectAllChecked(allRemainingSelected);
  };

  // Submit attendance to backend
  const handleSubmitAttendance = async () => {
    if (selectedSubject === "-- Select --" || selectedPeriod === "-- Select --") {
      toast.error("Please select both a subject and period.");
      return;
    }
    
    // If attendance already exists, allow editing without requiring total class increment
    if (!attendanceExists && !canAddAttendance) {
      toast.error("Please increment total classes first to enable attendance marking");
      return;
    }
    
    const attendanceArray = Object.values(markedAttendance);
    if (attendanceArray.length === 0) {
      toast.error("No students selected for attendance.");
      return;
    }
    
    setLoading(true);
    toast.loading(attendanceExists ? "Updating attendance..." : "Submitting attendance...");
    
    try {
      let response;
      
      if (attendanceExists) {
        // If attendance exists, we need to update existing records
        // First, remove existing attendance for this date/subject/period/section
        const existingEnrollmentNos = existingAttendanceRecords.map(record => record.enrollmentNo);
        
        // Remove existing attendance
        await axios.post(`${baseApiURL()}/attendence/removeBulk`, 
          existingAttendanceRecords.map(record => ({
            enrollmentNo: record.enrollmentNo,
            subject: record.subject,
            semester: record.semester,
            period: record.period,
            section: record.section,
            date: record.date
          }))
        );
        
        // Add new attendance
        response = await axios.post(
          `${baseApiURL()}/attendence/addBulk`,
          attendanceArray
        );
      } else {
        // Add new attendance
        response = await axios.post(
          `${baseApiURL()}/attendence/addBulk`,
          attendanceArray
        );
      }
      
      toast.dismiss();
      setLoading(false);
      
      if (response.data.success) {
        toast.success(attendanceExists ? "Attendance updated successfully!" : "Attendance submitted successfully!");
        setMarkedAttendance({});
        setSelectAllChecked(false);
        setAttendanceExists(false);
        setExistingAttendanceRecords([]);
        
        // Refresh the attendance check
        setTimeout(() => {
          checkAttendanceExists();
        }, 1000);
      } else {
        toast.error(response.data.message || "Failed to submit attendance.");
      }
    } catch (error) {
      toast.dismiss();
      setLoading(false);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to submit attendance.");
      }
      console.error("Submit attendance error:", error);
    }
  };

  return (
    <div className="w-full space-y-6">
      {/* Filters */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xs p-5 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
          <div>
            <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Branch</label>
            <select
              value={selectedBranch}
              onChange={handleBranchChange}
              disabled={!!lockedBranch}
              className={`w-full p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white ${lockedBranch ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <option>-- Select --</option>
              {branch.map((b) => (
                <option key={b._id} value={b.name}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Semester</label>
            <select
              value={semester}
              onChange={handleSemesterChange}
              className="w-full p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white"
            >
              <option>-- Select --</option>
              {[...Array(8).keys()].map((i) => (
                <option key={i + 1} value={i + 1}>
                  Semester {i + 1}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Regulation</label>
            <input
              type="text"
              value={selectedRegulation}
              readOnly
              placeholder="Detecting..."
              className="w-full p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 cursor-not-allowed font-bold text-indigo-600 dark:text-indigo-400"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Section</label>
            <select
              value={selectedSection}
              onChange={handleSectionChange}
              className="w-full p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white"
            >
              <option>-- Select --</option>
              {sections.map((section) => (
                <option key={section} value={section}>
                  Section {section}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Subject</label>
            <select
              value={selectedSubject}
              onChange={handleSubjectChange}
              className="w-full p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white"
              disabled={semester === "-- Select --"}
            >
              <option>-- Select --</option>
              {filteredSubjects.map((subject) => (
                <option key={subject._id} value={subject.name}>
                  {subject.name} {subject.code ? `(${subject.code})` : ''}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Period</label>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="w-full p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white"
            >
              <option>-- Select --</option>
              {[...Array(9).keys()].map((i) => (
                <option key={i + 1} value={i + 1}>
                  Period {i + 1}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Date</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Range</label>
            <div className="flex space-x-1">
              <input
                type="number"
                placeholder="From"
                value={range.start}
                onChange={(e) => setRange({ ...range, start: e.target.value })}
                className="w-full p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white"
              />
              <input
                type="number"
                placeholder="To"
                value={range.end}
                onChange={(e) => setRange({ ...range, end: e.target.value })}
                className="w-full p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white"
              />
            </div>
          </div>
        </div>

        {noStudentsMessage && (
          <div className="p-2 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-xl text-amber-800 dark:text-amber-300 text-xs flex items-center space-x-2 w-full">
            <FiAlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <span className="font-medium">{noStudentsMessage}</span>
          </div>
        )}

        {/* Total Classes & Absentees Controls */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-slate-100 dark:border-slate-800">
          <div>
            <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">
              Total Classes for Section {selectedSection !== "-- Select --" ? selectedSection : ""}
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                value={totalClasses}
                onChange={handleTotalClassesChange}
                className="flex-1 p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white"
                placeholder="Enter total classes"
                disabled={!selectedSubjectId || selectedSection === "-- Select --"}
              />
              <button
                onClick={incrementTotalClasses}
                disabled={!selectedSubjectId || selectedSection === "-- Select --" || loading}
                className="px-3 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white font-bold text-xs rounded-xl shadow-xs cursor-pointer"
              >
                +
              </button>
              <button
                onClick={decrementTotalClasses}
                disabled={!selectedSubjectId || selectedSection === "-- Select --" || loading || Number(totalClasses) <= 0}
                className="px-3 py-2 bg-rose-600 hover:bg-rose-500 disabled:opacity-40 text-white font-bold text-xs rounded-xl shadow-xs cursor-pointer"
              >
                −
              </button>
              <button
                onClick={updateTotalClassesManually}
                disabled={!selectedSubjectId || selectedSection === "-- Select --" || loading}
                className="px-3 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white font-bold text-xs rounded-xl shadow-xs cursor-pointer"
              >
                Update
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Absentees (Comma Separated)</label>
            <input
              type="text"
              value={absenteesInput}
              onChange={(e) => handleAbsenteesInput(e.target.value)}
              placeholder="e.g., 22N81A0501, 22N81A0502"
              className="w-full p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white"
            />
          </div>
        </div>
      </div>

      {/* Status indicator */}
      {attendanceExists && (
        <div className="p-3 bg-indigo-50 dark:bg-slate-950 border border-indigo-200 dark:border-slate-800 rounded-2xl text-xs text-indigo-900 dark:text-indigo-200">
          <strong>Attendance already exists</strong> for this date/subject/period/section. You can edit the existing attendance by modifying the selections below.
        </div>
      )}
      
      {!attendanceExists && canAddAttendance && (
        <div className="p-3 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 rounded-2xl text-xs text-emerald-800 dark:text-emerald-300 font-bold">
          ✓ Attendance marking is enabled. You can now mark attendance for students.
        </div>
      )}

      {/* Bulk Actions */}
      <div className="flex justify-between items-center">
        <button
          onClick={toggleSelectAll}
          disabled={(!attendanceExists && !canAddAttendance) || selectedSubject === "-- Select --" || selectedPeriod === "-- Select --" || loading}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition cursor-pointer shadow-xs ${
            (!attendanceExists && !canAddAttendance) || selectedSubject === "-- Select --" || selectedPeriod === "-- Select --" || loading 
              ? 'bg-slate-200 dark:bg-slate-800 text-slate-400 cursor-not-allowed' 
              : selectAllChecked 
                ? 'bg-rose-600 hover:bg-rose-500 text-white' 
                : 'bg-emerald-600 hover:bg-emerald-500 text-white'
          }`}
        >
          {loading ? 'Processing...' : selectAllChecked ? 'Unmark All' : 'Mark All'}
        </button>

        <button
          onClick={handleSubmitAttendance}
          disabled={(!attendanceExists && !canAddAttendance) || Object.keys(markedAttendance).length === 0 || loading}
          className={`px-6 py-2 rounded-xl text-xs font-bold text-white transition cursor-pointer shadow-xs ${
            (!attendanceExists && !canAddAttendance) || Object.keys(markedAttendance).length === 0 || loading 
              ? 'bg-slate-200 dark:bg-slate-800 text-slate-400 cursor-not-allowed' 
              : 'bg-indigo-600 hover:bg-indigo-500'
          }`}
        >
          {loading ? (attendanceExists ? "Updating..." : "Submitting...") : (attendanceExists ? "Update Attendance" : "Submit Attendance")}
        </button>
      </div>

      {/* Students Table */}
      {loading ? (
        <div className="flex justify-center items-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xs overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
              <thead className="bg-slate-50 dark:bg-slate-950">
                <tr>
                  <th className="px-5 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectAllChecked}
                      onChange={toggleSelectAll}
                      disabled={(!attendanceExists && !canAddAttendance) || selectedSubject === "-- Select --" || selectedPeriod === "-- Select --"}
                      className="rounded text-indigo-600 focus:ring-indigo-500"
                    />
                  </th>
                  <th className="px-5 py-3 text-left text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Enrollment No</th>
                  <th className="px-5 py-3 text-left text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Name</th>
                  <th className="px-5 py-3 text-left text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Branch</th>
                  <th className="px-5 py-3 text-left text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Section</th>
                  <th className="px-5 py-3 text-left text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Semester</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-xs">
                {filteredStudents.map((student) => {
                  const isAbsent = absenteesInput.split(',').map(e => e.trim()).filter(e => e).includes(student.enrollmentNo);
                  return (
                    <tr key={student._id} className={isAbsent ? 'bg-rose-50/60 dark:bg-rose-950/30' : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'}>
                      <td className="px-5 py-3">
                        <input
                          type="checkbox"
                          checked={!!markedAttendance[student.enrollmentNo]}
                          onChange={() => toggleAttendance(student)}
                          disabled={(!attendanceExists && !canAddAttendance) || selectedSubject === "-- Select --" || selectedPeriod === "-- Select --" || isAbsent}
                          className="rounded text-indigo-600 focus:ring-indigo-500"
                        />
                      </td>
                      <td className="px-5 py-3 font-mono font-bold text-indigo-600 dark:text-indigo-400">
                        {student.enrollmentNo}
                      </td>
                      <td className="px-5 py-3 font-medium text-slate-900 dark:text-white">
                        {student.firstName} {student.middleName} {student.lastName}
                      </td>
                      <td className="px-5 py-3 text-slate-600 dark:text-slate-400">{student.branch}</td>
                      <td className="px-5 py-3 text-slate-600 dark:text-slate-400">{student.section}</td>
                      <td className="px-5 py-3 text-slate-600 dark:text-slate-400">{student.semester}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddAttendance;